<template>
  <div class="page-stack">
    <section class="section-shell section-shell--hero">
      <div class="section-head">
        <div>
          <div class="section-kicker">Etsy</div>
          <h3 class="section-title">Etsy 发布记录</h3>
          <p class="section-subtitle">直接从 Etsy 店铺拉取 listing 记录，并与系统内已发布产品做对照。</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <select v-model="state" class="field-select min-w-[180px]">
            <option value="all">全部状态</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="sold_out">Sold Out</option>
            <option value="expired">Expired</option>
          </select>
          <button class="btn btn-secondary" @click="loadListings">刷新记录</button>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Compare</div>
          <h3 class="section-title">平台记录对照</h3>
        </div>
        <div class="text-sm text-slate-400">共 {{ pagination.total }} 条</div>
      </div>
      <div class="table-shell mt-6 overflow-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th>Listing</th>
              <th>价格 / 库存</th>
              <th>Section / Taxonomy</th>
              <th>本地对照</th>
              <th>时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in listings" :key="item.listingId">
              <td class="min-w-[320px]">
                <div class="font-semibold text-slate-900">{{ item.title || `Listing #${item.listingId}` }}</div>
                <div class="mt-2 text-sm text-slate-500">ID：{{ item.listingId }}</div>
                <div class="mt-3">
                  <span class="badge" :class="badgeClass(item.state)">{{ item.state || '-' }}</span>
                </div>
              </td>
              <td class="min-w-[180px] text-sm text-slate-600">
                <div>价格：{{ formatPrice(item) }}</div>
                <div class="mt-2">库存：{{ item.quantity ?? '-' }}</div>
              </td>
              <td class="min-w-[220px] text-sm text-slate-600">
                <div>Section ID：{{ item.shopSectionId || '-' }}</div>
                <div class="mt-2">Taxonomy ID：{{ item.taxonomyId || '-' }}</div>
              </td>
              <td class="min-w-[260px] text-sm text-slate-600">
                <div v-if="item.localProductId">
                  <div class="font-semibold text-slate-900">{{ item.localProductTitle || `产品 #${item.localProductId}` }}</div>
                  <div class="mt-2">本地产品 ID：{{ item.localProductId }}</div>
                  <div class="mt-2">状态：{{ item.localGenerationStatus || '-' }}</div>
                </div>
                <div v-else class="text-slate-400">系统内未找到对应发布记录</div>
              </td>
              <td class="min-w-[180px] text-sm text-slate-500">
                <div>创建：{{ item.createdAt || '-' }}</div>
                <div class="mt-2">更新：{{ item.updatedAt || '-' }}</div>
              </td>
              <td>
                <div class="flex flex-wrap gap-2">
                  <button class="btn btn-secondary" @click="openDetail(item.listingId)">明细</button>
                  <a
                    v-if="item.url"
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn btn-secondary"
                  >
                    打开 Etsy
                  </a>
                </div>
              </td>
            </tr>
            <tr v-if="!listings.length">
              <td colspan="6" class="table-empty">当前没有 Etsy 平台记录</td>
            </tr>
          </tbody>
        </table>
      </div>
      <AppPagination
        :page-num="pagination.pageNum"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :total-pages="pagination.totalPages"
        @change-page="handlePageChange"
        @change-size="handlePageSizeChange"
      />
    </section>

    <div v-if="detailVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel modal-panel--lg" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">Etsy 记录明细</h4>
            <p class="mt-2 text-sm text-slate-500">这里展示 Etsy 平台返回的结构化内容和原始数据，便于和系统内产品对照。</p>
          </div>
          <button class="btn btn-secondary" @click="closeDetail">关闭</button>
        </div>

        <div v-if="detail" class="modal-panel__body mt-6">
          <div class="grid gap-4 md:grid-cols-2">
            <div class="settings-card">
              <div class="field-label">Listing ID</div>
              <div class="text-sm text-slate-700">{{ detail.listingId || '-' }}</div>
            </div>
            <div class="settings-card">
              <div class="field-label">状态</div>
              <div class="text-sm text-slate-700">{{ detail.state || '-' }}</div>
            </div>
            <div class="settings-card">
              <div class="field-label">价格</div>
              <div class="text-sm text-slate-700">{{ formatPrice(detail) }}</div>
            </div>
            <div class="settings-card">
              <div class="field-label">库存</div>
              <div class="text-sm text-slate-700">{{ detail.quantity ?? '-' }}</div>
            </div>
            <div class="settings-card">
              <div class="field-label">Section ID</div>
              <div class="text-sm text-slate-700">{{ detail.shopSectionId || '-' }}</div>
            </div>
            <div class="settings-card">
              <div class="field-label">Taxonomy ID</div>
              <div class="text-sm text-slate-700">{{ detail.taxonomyId || '-' }}</div>
            </div>
          </div>

          <div class="mt-4">
            <label class="field-label">标题</label>
            <div class="rounded-[1rem] border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-700">
              {{ detail.title || '-' }}
            </div>
          </div>

          <div class="mt-4">
            <label class="field-label">原始返回</label>
            <pre class="code-panel">{{ prettyRawData }}</pre>
          </div>
        </div>

        <div class="modal-panel__footer">
          <button class="btn btn-secondary" @click="closeDetail">关闭</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import AppPagination from '@/components/AppPagination.vue';
import { etsyApi } from '@/api';
import { notifyError } from '@/utils/feedback';

const listings = ref([]);
const state = ref('all');
const detail = ref(null);
const detailVisible = ref(false);
const pagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0
});

const prettyRawData = computed(() => JSON.stringify(detail.value?.rawData || {}, null, 2));

function badgeClass(value) {
  if (value === 'active') {
    return 'badge-success';
  }
  if (value === 'draft') {
    return 'badge-muted';
  }
  return 'badge-warm';
}

function formatPrice(item) {
  if (!item || item.price === null || item.price === undefined || item.price === '') {
    return '-';
  }
  return `${item.price} ${item.currencyCode || ''}`.trim();
}

async function loadListings() {
  try {
    const res = await etsyApi.listListings({
      state: state.value,
      pageNum: pagination.pageNum,
      pageSize: pagination.pageSize
    });
    listings.value = res.data.records || [];
    pagination.total = res.data.total || 0;
    pagination.totalPages = res.data.totalPages || 0;
  } catch (error) {
    notifyError(error.message || '读取 Etsy 平台记录失败');
  }
}

async function openDetail(listingId) {
  try {
    const res = await etsyApi.listingDetail(listingId);
    detail.value = res.data || null;
    detailVisible.value = true;
  } catch (error) {
    notifyError(error.message || '读取 Etsy 明细失败');
  }
}

function closeDetail() {
  detailVisible.value = false;
  detail.value = null;
}

function handlePageChange(pageNum) {
  pagination.pageNum = pageNum;
  loadListings();
}

function handlePageSizeChange(pageSize) {
  pagination.pageSize = pageSize;
  pagination.pageNum = 1;
  loadListings();
}

watch(state, () => {
  pagination.pageNum = 1;
  loadListings();
});

onMounted(loadListings);
</script>
